<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      Fingerprints and bite marks aren’t the only types of ‘prints’ forensic scientists are
      interested in at a crime scene. Footwear and tyre tracks can also be helpful if they can be
      traced to their owners. In this activity, we’ll take a closer look at shoe tracks, but much of
      the information shared here applies equally to tyre tracks too.
    </p>
    <p class="mb-5">We can divide prints into three types:</p>
    <ul class="default-list">
      <li>Visible</li>
      <li>Plastic</li>
      <li>Latent</li>
    </ul>
    <table class="table default-table">
      <tr class="subheader">
        <th scope="col" width="30%">Type of Print</th>
        <th scope="col" width="30%">Definition</th>
        <th scope="col" width="40%">Example</th>
      </tr>
      <tr class="body">
        <th scope="row" class="subheader">Visible</th>
        <td>A visible print is a transfer of material from the shoe to a surface.</td>
        <td>Bloody shoe prints are seen on the floor, walking away from the body.</td>
      </tr>
      <tr class="body">
        <th scope="row" class="subheader">Plastic</th>
        <td>
          A plastic print is a three-dimensional impression left on a soft surface. This includes
          shoe tracks left in sand, mud, or snow.
        </td>
        <td>The tracks of the suspect’s shoes can be seen in the mud at the crime scene.</td>
      </tr>
      <tr class="body">
        <th scope="row" class="subheader">Latent</th>
        <td>
          A latent print is one that is not readily visible to the naked eye. This type is created
          through static charges between the sole or tread and the surface.
        </td>
        <td>
          Though there are no shoe prints visible to the naked eye, when the glass walkway is dusted
          with a special powder, shoe prints are revealed.
        </td>
      </tr>
    </table>
    <h4 class="mb-5">Task 1: Shoe Print Analysis</h4>

    <p class="mb-5">
      Now, it’s time to get some shoe print analysis practice! To get started, you’ll need to locate
      a plastic shoe print to analyse. A plastic print is a three-dimensional print that occurs when
      a shoe sinks into soft ground such as sand, mud, or even snow.
    </p>
    <p class="mb-5">There are a couple of options for finding a suitable print:</p>
    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <p class="mb-5">
          <strong>Option 1:</strong> Take a look in your yard for a plastic print a family member
          made already.
        </p>
        <p class="mb-5">
          <strong>Option 2:</strong> Have a family member put on a pair of shoes secretly, wet some
          ground, and leave some footprints in the garden or on a piece of public land.
        </p>
        <p class="mb-5">
          You are looking here for a shoe print from someone in your house, but you shouldn’t know
          which shoes the prints come from before you start doing your shoe print analysis.
        </p>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <img
          src="@/assets/images/experiences/forensic_science/footprint.jpg"
          alt="A plastic shoe print pressed into snow and leaves, showing the sole's circular and wavy tread pattern."
          class="mx-auto mb-5 img-responsive"
        />
      </div>
    </div>
    <p class="mb-5">
      Once you have located a suitable print, it’s time for the next stage. A plastic shoe print
      would normally be first photographed, and then a cast would be made of the impression. For our
      purposes, a photograph of the print will be enough. Follow the advice below to get the best
      possible photograph of your print.
    </p>
    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <p class="mb-5">Tips for Photographing the Shoe Print</p>
        <ul class="default-list">
          <li>Use a flash to help clearly define features, even in sunlight.</li>
          <li>Place a ruler alongside the print to give proper scale and size.</li>
          <li>
            Write identification details such as date, location, name of the investigator on a piece
            of paper and place near the print and include in the shot.
          </li>
        </ul>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <img
          src="@/assets/images/experiences/forensic_science/shoeprint_2.jpg"
          alt="Two shoe prints in sand photographed with a black and white forensic ruler placed alongside them for scale."
          class="mx-auto mb-5 img-responsive"
        />
      </div>
    </div>
    <p class="mb-5">Hold on to that photo – you will be asked to upload it shortly.</p>

    <h4 class="mb-5">Task 2: Shoe Print Measurements</h4>
    <p class="mb-5">
      Taking some basic measurements of the shoe print will help you identify which shoe the print
      comes from. Using a ruler or tape measure, measure the length, width, and depth of your shoe
      print and record in the table below—use millimetres to ensure a high level of accuracy.
    </p>

    <table class="table default-table">
      <tr class="subheader">
        <th scope="col" width="50%">Dimension</th>
        <th scope="col" width="50%">Measurement</th>
      </tr>
      <tr class="body" v-for="(item, index) in task2_measurements" :key="'task2_' + index">
        <th scope="row" class="subheader">{{ item }}</th>
        <td>
          <div class="overflow-hidden flex justify-center">
            <text-block
              v-model="data['question' + index]"
              :editable="isEditable"
              style="width: 100px; float: left"
              pclass="my-2"
            />
            <p class="float-left py-2 pl-2"><strong>mm</strong></p>
          </div>
        </td>
      </tr>
    </table>

    <h4 class="mb-5">Task 3: Shoe Identification</h4>

    <p class="mb-5">
      Now, gather together all the shoes in your home. It might be best to do this at night when
      everyone is finished with them for the day! Using your ruler, eliminate all the shoes that are
      obviously not of the same measurements as above.
    </p>
    <p class="mb-5">
      ou might have a good idea of the shoes the print belongs to, but just to be sure, let’s learn
      how to take a ‘control’ from your suspect’s shoe.
    </p>
    <p class="mb-5">To do this, you will need:</p>
    <ul class="default-list">
      <li>A thick make-up brush</li>
      <li>Dark powder eg: cacao powder</li>
      <li>A sheet of sticky back plastic</li>
    </ul>
    <p class="mb-5">Watch the video below to learn how to do this procedure:</p>
    <responsive-youtube-wrapper :video="videos[1].url" :image="videos[1].image" />

    <p class="mb-5">
      Now, you should have all the evidence you need to make a positive ID. Once that’s done, take a
      photo of the impression you made and the shoe it came from.
    </p>

    <p class="mb-5">Finally, to complete this activity, you need to upload 3 photos:</p>
    <ol class="numbered-list">
      <li>Plastic Print</li>
      <li>Shoe Impression</li>
      <li>The Shoe</li>
    </ol>

    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_1']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Plastic Print Photo'"
      index="1"
      filetype="media"
    />
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_2']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Shoe Impression Photo'"
      index="2"
      filetype="media"
    />
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_3']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Shoe Photo'"
      index="3"
      filetype="media"
    />

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/forensic_science/shoeprint_splash.jpg'),
          url: 'https://www.youtube.com/embed/QeXNM0nj0qI',
        },
      },
      image1: require('@/assets/images/experiences/fitness/HIIT-Formula-Infographic-1.jpg'),
      uploaded_files: {
        uploaded_file_1: '',
        uploaded_file_2: '',
        uploaded_file_3: '',
      },
      task2_measurements: ['Length of Print', 'Width of Print', 'Depth of Print'],
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.uploaded_files !== 'undefined') {
      for (let i = 1; i <= Object.keys(this.uploaded_files).length; i++) {
        if (typeof this.data['uploaded_file_' + i] !== 'undefined') {
          this.uploaded_files['uploaded_file_' + i] = this.data['uploaded_file_' + i];
        }
      }
    }
  },
};
</script>
